import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studentService } from '../../services/studentService';

const StudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    loadStudent();
  }, [id]);

  const loadStudent = async () => {
    setLoading(true);
    try {
      const response = await studentService.getStudentById(id);
      if (response.success) {
        setStudent(response.data);
      } else {
        console.error('Error al cargar alumno:', response.message);
      }
    } catch (error) {
      console.error('Error al cargar alumno:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'personal', name: 'Información Personal', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { id: 'record', name: 'Hoja de Vida', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { id: 'evaluations', name: 'Evaluaciones', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Alumno no encontrado</h2>
        <button
          onClick={() => navigate('/students')}
          className="text-primary-600 hover:text-primary-800"
        >
          Volver al listado
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/students')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver al listado
        </button>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-primary-500 to-primary-600"></div>
        <div className="px-6 pb-6">
          <div className="flex items-end -mt-16 mb-6">
            <img
              src={student.avatar}
              alt={student.name}
              className="w-32 h-32 rounded-full border-4 border-white shadow-lg"
            />
            <div className="ml-6 pb-2">
              <h1 className="text-2xl font-bold text-gray-900">{student.name}</h1>
              <div className="flex items-center space-x-3 mt-2">
                <span className="px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800">
                  {student.course || 'Sin curso'}
                </span>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                  student.status === 'activo'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {student.status === 'activo' ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Tab Headers */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                </svg>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'personal' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Información Personal */}
              <div className="lg:col-span-2">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Información Personal</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Nombre Completo</label>
                    <p className="mt-1 text-sm text-gray-900">{student.name}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">RUT</label>
                    <p className="mt-1 text-sm text-gray-900">{student.rut}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <a
                      href={`mailto:${student.email}`}
                      className="mt-1 text-sm text-blue-600 hover:text-blue-800 hover:underline block"
                    >
                      {student.email}
                    </a>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Teléfono</label>
                    <a
                      href={`tel:${student.phone}`}
                      className="mt-1 text-sm text-green-600 hover:text-green-800 hover:underline block"
                    >
                      {student.phone}
                    </a>
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-500">Dirección</label>
                    <p className="mt-1 text-sm text-gray-900">{student.address || 'No especificada'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Fecha de Registro</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(student.createdAt).toLocaleDateString('es-CL', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Apoderados */}
              <div className="lg:col-span-1">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Apoderados</h2>
                {student.guardians && student.guardians.length > 0 ? (
                  <div className="space-y-4">
                    {student.guardians.map((guardian) => (
                      <div key={guardian.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <h3 className="font-medium text-gray-900 mb-2">{guardian.name}</h3>

                        <div className="space-y-2">
                          <div className="flex items-center text-sm">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <a
                              href={`mailto:${guardian.email}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {guardian.email}
                            </a>
                          </div>

                          <div className="flex items-center text-sm">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <a
                              href={`tel:${guardian.phone}`}
                              className="text-green-600 hover:text-green-800 hover:underline"
                            >
                              {guardian.phone}
                            </a>
                          </div>

                          {guardian.address && (
                            <div className="flex items-start text-sm">
                              <svg className="w-4 h-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="text-gray-700">{guardian.address}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-sm text-gray-500">Sin apoderados asignados</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'record' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Anotaciones del Alumno</h2>
                <div className="text-sm text-gray-500">
                  Total: <span className="font-medium">{student.annotations?.length || 0}</span> anotaciones
                </div>
              </div>

              {student.annotations && student.annotations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Curso
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Título
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Descripción
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {student.annotations.map((annotation, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(annotation.date).toLocaleDateString('es-CL')}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {annotation.course}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              annotation.type === 'positiva'
                                ? 'bg-green-100 text-green-800'
                                : annotation.type === 'negativa'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {annotation.type.charAt(0).toUpperCase() + annotation.type.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm font-medium text-gray-900">
                            {annotation.title}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {annotation.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Sin anotaciones registradas</h3>
                  <p className="text-gray-600">Este alumno no tiene anotaciones en su hoja de vida</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'evaluations' && (
            <div className="space-y-8">
              {/* Historial de Cursos */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Historial de Cursos</h2>
                {student.courseHistory && student.courseHistory.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {student.courseHistory.map((course, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-500">{course.year}</span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            course.status === 'aprobado'
                              ? 'bg-green-100 text-green-800'
                              : course.status === 'reprobado'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {course.status.charAt(0).toUpperCase() + course.status.slice(1)}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{course.course}</h3>
                        {course.average && (
                          <p className="text-sm text-gray-600">Promedio: <span className="font-medium">{course.average}</span></p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">Sin historial de cursos registrado</p>
                  </div>
                )}
              </div>

              {/* Proyectos Participados */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Proyectos ABP</h2>
                {student.projects && student.projects.length > 0 ? (
                  <div className="space-y-4">
                    {student.projects.map((project, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                project.status === 'completado'
                                  ? 'bg-green-100 text-green-800'
                                  : project.status === 'en_progreso'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {project.status === 'completado' ? 'Completado' : project.status === 'en_progreso' ? 'En Progreso' : 'Pendiente'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{project.description}</p>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {project.period}
                              </div>
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                {project.course}
                              </div>
                            </div>
                          </div>
                          {project.grade && (
                            <div className="ml-4 text-right">
                              <div className="text-sm text-gray-500 mb-1">Calificación</div>
                              <div className="text-2xl font-bold text-primary-600">{project.grade}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Sin proyectos registrados</h3>
                    <p className="text-gray-600">Este alumno no ha participado en proyectos ABP</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDetail;
